// src/utils/ownerSession.ts
//
// Persists the owner onboarding session token in sessionStorage keyed by slug,
// so a page refresh within the token lifetime (~2h) keeps the owner logged in.

interface StoredOwnerSession {
    token: string;
    expiresAt: string; // ISO datetime
}

const keyFor = (slug: string) => `ownerOnboardingSession:${slug}`;

export function saveOwnerSession(slug: string, token: string, expiresAt: string): void {
    try {
        const payload: StoredOwnerSession = { token, expiresAt };
        sessionStorage.setItem(keyFor(slug), JSON.stringify(payload));
    } catch {
        /* sessionStorage may be unavailable (private mode); ignore. */
    }
}

/** Returns a still-valid session token for the slug, or null if missing/expired. */
export function getOwnerSession(slug: string): string | null {
    try {
        const raw = sessionStorage.getItem(keyFor(slug));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredOwnerSession;
        if (!parsed?.token) return null;
        if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() <= Date.now()) {
            clearOwnerSession(slug);
            return null;
        }
        return parsed.token;
    } catch {
        return null;
    }
}

export function clearOwnerSession(slug: string): void {
    try {
        sessionStorage.removeItem(keyFor(slug));
    } catch {
        /* ignore */
    }
}
